import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";
import { loadAuthClientSetting } from "@/lib/auth/runtimeConfig";
import { listQueryOptions } from "@/lib/listQuery";
import type { AccessGrantResult, AccessUser, ResendInviteView } from "@shared/types/access";
import type { AuthIssuerView } from "@shared/types/accessAdmin";
import type { AuthClientSetting } from "@shared/types/authClientConfig";
import type { SignInMethod } from "@shared/types/signInMethod";
import type { RoleName, ScopeType } from "@shared/permissions";
import { ROLE_RANK, ROLE_SCOPES } from "@shared/permissions";
import { trimRunEnd } from "@shared/trimRuns";

/** `pending | active | disabled`, taken from the view rather than restated. */
type UserStatus = AccessUser["status"];

/**
 * Users and access, at whichever scope the screen is looking at (FR-11, SEC-18).
 *
 * The three surfaces answer the SAME user view — `accessUserSchema` is reused by
 * `adminUserSchema` server-side — so one hook, one grid and one dialog cover all
 * three scopes and a rule can only be written once. What differs is the path, and
 * which mutations that path offers; both are decided here rather than in the
 * screen, so a component never builds a URL.
 *
 * `includeInactive` is always true. FR-11 deactivates access and never deletes it,
 * so a list that hid revoked grants would quietly contradict the promise the empty
 * state makes; the grid shows them as Disabled with a Reactivate action instead.
 */

export type AccessScope = "instance" | "tenant" | "platform";

/** One GRANT, joined to the person who holds it — the unit the grid renders. */
export interface AccessRow {
  /** Group access: carried from the person, so the people view can be rebuilt from rows. */
  createdVia?: "grant" | "group";
  groupsClaimState?: AccessUser["groupsClaimState"] | null;
  /** #1639 C1: the person's email sign-in account request, when there is one. */
  accountCreation?: AccessUser["accountCreation"];
  grantId: string;
  userId: string;
  email: string;
  displayName: string | null;
  userStatus: UserStatus;
  lastSignInAt: string | null;
  /** Admin MFA: whether the last recorded sign-in proved a second factor; null before one. */
  lastSignInMfa?: boolean | null;
  /**
   * #1017: the last authenticated web request; null before the first one, and undefined when the
   * server did not answer it (a reader who cannot manage users), which hides the column.
   */
  lastActiveAt?: string | null;
  roleName: RoleName;
  scopeType: ScopeType;
  tenantId: string | null;
  instanceId: string | null;
  isActive: boolean;
  grantedAt: string;
  revokedAt: string | null;
  /** SEC-18: the signed-in user is looking at their own grant. */
  isSelf: boolean;
  /** Session 9: the issuer the person is bound to, and the one an unbound row is pinned to. */
  idpIssuer: string | null;
  expectedIdpIssuer: string | null;
  /** #1639 C0: how the person signs in, from the server; null when it cannot say. */
  signInMethod?: SignInMethod | null;
  /** #1639 C0: an Email person pending, with no invitation sent yet. */
  inviteWaiting?: boolean;
  /** #1678: an Email person pending, invited while skyData made no account for them. */
  inviteUnconfirmed?: boolean;
  /**
   * Group access: `group` is the default role a directory group carries (not a grant: it has no
   * revoke), `override` a role an admin raised this person to while they stay in the group.
   * Undefined for a direct grant.
   */
  source?: "group" | "override";
  mappingId?: string | null;
  mappingLabel?: string | null;
}

/**
 * One PERSON and every grant of theirs that reaches the scope in view — the unit
 * the grid renders. The server already answers per user; a row per grant made
 * one person holding a platform, a tenant and an instance grant read as three
 * people.
 */
export interface AccessPerson {
  userId: string;
  email: string;
  displayName: string | null;
  userStatus: UserStatus;
  lastSignInAt: string | null;
  /** Admin MFA: whether the last recorded sign-in proved a second factor; null before one. */
  lastSignInMfa?: boolean | null;
  /** #1017: as on `AccessRow`; undefined when the server did not answer it. */
  lastActiveAt?: string | null;
  isSelf: boolean;
  idpIssuer: string | null;
  expectedIdpIssuer: string | null;
  /** #1639 C0: how the person signs in; null when the server cannot say. */
  signInMethod: SignInMethod | null;
  /** #1639 C0: an Email person pending, with no invitation sent yet. */
  inviteWaiting: boolean;
  /** #1678: an Email person pending, invited while skyData made no account for them. */
  inviteUnconfirmed: boolean;
  /** Group access: the row was created at first sign-in by a directory group. */
  createdVia: "grant" | "group";
  /** Group access: what the person's last recorded token said about their groups. */
  groupsClaimState: AccessUser["groupsClaimState"] | null;
  /** #1639 C1: the person's email sign-in account request, when there is one. */
  accountCreation?: AccessUser["accountCreation"];
  /** Each grant keeps its own revoke and reactivate: access is audited per grant. */
  grants: AccessRow[];
  /**
   * The highest ACTIVE role among those grants, which is what the resolver
   * enforces (`server/auth/accessResolver.ts`); null when nothing is in force.
   */
  effectiveRole: RoleName | null;
}

export interface AccessListFilters {
  search?: string;
  role?: RoleName | "all";
  status?: "all" | "pending" | "active" | "disabled";
}

/** The body a grant takes, before the scope surface decides where it is posted. */
export interface GrantInput {
  email: string;
  displayName?: string;
  roleName: RoleName;
  reason?: string;
  /** Platform surface only: the identity provider the new person is pinned to (S9 §5.4). */
  expectedIssuer?: string;
  /** #1639 C0: the dialog's "Sign in with", sent by every surface when the dialog offers it. */
  signInMethod?: SignInMethod;
  /**
   * #1275: the grantor confirmed changing the person's current role at this scope. One person
   * holds one direct role per scope, so the server replaces the current one only with this.
   */
  replaceExisting?: boolean;
}

/** Moving one person to another identity provider (S9 §5.3). */
export interface RebindInput {
  userId: string;
  toIssuer: string;
  reason: string;
  /** Required by the server when the person is the signed-in user. */
  allowSelf?: boolean;
}

/** The issuers the API trusts, for the rebind and grant dialogs (platform surface only). */
export const AUTH_ISSUERS_PATH = "/api/v2/platform/auth/issuers";

export function useAuthIssuers(enabled: boolean) {
  return useQuery<AuthIssuerView[]>({
    queryKey: [AUTH_ISSUERS_PATH],
    queryFn: ({ signal }) => apiRequest<AuthIssuerView[]>(AUTH_ISSUERS_PATH, { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * #1639 C0: the deployment's sign-in settings, for the grant dialog's "Sign in with" choice (which
 * methods exist, each provider's `pinEmailDomains`). The public `GET /api/auth/config`, read only
 * while the dialog is open; null when it cannot be read, and then the dialog offers no choice.
 */
export function useGrantSignInSetting(enabled: boolean) {
  return useQuery<AuthClientSetting | null>({
    queryKey: ["/api/auth/config", "grant-sign-in"],
    queryFn: ({ signal }) => loadAuthClientSetting(signal),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** The name an issuer URL is shown by: its configured name, else its host. */
export function issuerLabel(issuer: string, issuers: readonly AuthIssuerView[]): string {
  const normalize = (value: string) => trimRunEnd(value.trim(), "/").toLowerCase();
  const match = issuers.find((entry) => normalize(entry.issuer) === normalize(issuer));
  if (match?.name) return match.name;
  try {
    return new URL(issuer).host;
  } catch {
    return issuer;
  }
}

/**
 * What a person calls the provider behind each claim profile (fixes wave 3). An issuer's
 * configured name (`skypoint-extid-dev`) is an id for operators; the screens lead with the
 * provider. `custom` names no provider, so such an issuer is shown by its name, as before.
 *
 * Not the `label` of `AUTH_CLIENT`'s providers (`GET /api/auth/config`): that is the sign-in
 * button's text ("Sign in", "Sign in with Microsoft"), and it exists only for issuers the browser
 * signs in with, not for every issuer the API trusts.
 */
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  "entra-external": "External ID",
  "entra-workforce": "Microsoft",
};

/** How an issuer is shown: `text` leads, `id` (its configured name) goes beside it or in a title. */
export interface IssuerDisplay {
  /** The provider, or the issuer's name when the provider is unknown or shared. */
  text: string;
  /** The issuer's name when `text` does not already carry it; null otherwise. */
  id: string | null;
}

export function issuerDisplay(issuer: string, issuers: readonly AuthIssuerView[]): IssuerDisplay {
  const normalize = (value: string) => trimRunEnd(value.trim(), "/").toLowerCase();
  const name = issuerLabel(issuer, issuers);
  const match = issuers.find((entry) => normalize(entry.issuer) === normalize(issuer));
  const provider = match ? PROVIDER_LABELS[match.claims] : undefined;
  if (!provider) return { text: name, id: null };
  // Two issuers of one provider (two External ID tenants) would read the same: keep them apart.
  const shared = issuers.filter((entry) => PROVIDER_LABELS[entry.claims] === provider).length > 1;
  return shared ? { text: `${provider} (${name})`, id: null } : { text: provider, id: name };
}

/** A select option for an issuer: the provider with its name beside it; the provider alone once chosen. */
export function issuerOption(
  entry: AuthIssuerView,
  issuers: readonly AuthIssuerView[],
  value: string,
  suffix = "",
): { value: string; text: string; selectedText: string } {
  const shown = issuerDisplay(entry.issuer, issuers);
  const full = shown.id ? `${shown.text} (${shown.id})` : shown.text;
  return { value, text: `${full}${suffix}`, selectedText: shown.text };
}

/**
 * The instance header, stated by the screen rather than left to the fetch layer.
 *
 * `lib/api.ts` normally supplies `x-instance-id` from a getter that
 * `TenantInstanceProvider` publishes in a LAYOUT effect, which is what fixed the
 * original ordering bug: every layout effect, parents included, runs before any
 * child's passive effect, and TanStack Query starts its fetches from passive
 * effects. What remains is not a race but a plain fact about the data — the
 * getter answers null until `/api/v2/me` has resolved and an instance has been
 * chosen, so a screen that renders and queries before then would still send no
 * header and be refused with a 400 naming it. Screens gate on
 * `selectedInstance` for that reason and pass the header explicitly. When an instance is
 * selected, `buildHeaders` keeps the caller's header and sends the selected instance only when
 * the caller named none (#449, `api.headers.test.ts`). The server re-checks the header on every
 * request, so naming one grants nothing.
 */
/** A key nothing ever writes, for the list query while its scope has no path. */
const NO_LIST_KEY = "access-users:none";

export function instanceHeaders(
  instanceId: string | undefined,
): Record<string, string> | undefined {
  return instanceId ? { "x-instance-id": instanceId } : undefined;
}

function queryString(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

function usersBasePath(scope: AccessScope, tenantId: string | null): string {
  if (scope === "instance") return "/api/v2/access/users";
  if (scope === "tenant") return `/api/v2/tenants/${tenantId}/users`;
  return "/api/v2/platform/users";
}

/** One grant's route at each scope (revoke is this path; resending an invite appends to it). */
function grantPath(scope: AccessScope, tenantId: string | null, grantId: string): string {
  if (scope === "platform") return `/api/v2/platform/grants/${grantId}`;
  if (scope === "tenant") return `/api/v2/tenants/${tenantId}/grants/${grantId}`;
  return `/api/v2/access/grants/${grantId}`;
}

/**
 * The list path for a scope, or null when the tenant surface has no tenant yet.
 *
 * Null rather than a path with an empty id in it: a disabled TanStack query
 * still reads its cache key, so a half-built path is a key some other query
 * could one day write, and the screen would show rows no request had fetched.
 */
export function accessUsersPath(
  scope: AccessScope,
  tenantId: string | null,
  filters: AccessListFilters = {},
): string | null {
  if (scope === "tenant" && !tenantId) return null;
  const base = usersBasePath(scope, tenantId);
  // Role is filtered in the browser: it lives on the grant rather than on the
  // user, so no list route takes it.
  return `${base}${queryString({
    includeInactive: "true",
    search: filters.search,
    status:
      scope === "platform" && filters.status && filters.status !== "all"
        ? filters.status
        : undefined,
  })}`;
}

/** The scope a role is assignable at (`ROLE_SCOPES`), which is what the dialog shows. */
export function scopeForRole(role: RoleName): ScopeType {
  return ROLE_SCOPES[role][0] ?? "instance";
}

export function toRows(users: AccessUser[], selfEmail: string | undefined): AccessRow[] {
  const rows: AccessRow[] = [];
  for (const user of users) {
    for (const grant of user.grants) {
      rows.push({
        grantId: grant.id,
        userId: user.id,
        email: user.email,
        displayName: user.displayName,
        userStatus: user.status,
        lastSignInAt: user.lastSignInAt,
        lastSignInMfa: user.lastSignInMfa ?? null,
        ...(user.lastActiveAt !== undefined ? { lastActiveAt: user.lastActiveAt } : {}),
        roleName: grant.roleName,
        scopeType: grant.scopeType,
        tenantId: grant.tenantId,
        instanceId: grant.instanceId,
        isActive: grant.isActive,
        grantedAt: grant.grantedAt,
        revokedAt: grant.revokedAt,
        isSelf: Boolean(selfEmail) && user.email.toLowerCase() === selfEmail?.toLowerCase(),
        idpIssuer: user.idpIssuer ?? null,
        expectedIdpIssuer: user.expectedIdpIssuer ?? null,
        signInMethod: user.signInMethod ?? null,
        inviteWaiting: user.inviteWaiting ?? false,
        inviteUnconfirmed: user.inviteUnconfirmed ?? false,
        source: grant.source,
        mappingId: grant.mappingId ?? null,
        mappingLabel: grant.mappingLabel ?? null,
        createdVia: user.createdVia ?? "grant",
        groupsClaimState: user.groupsClaimState ?? null,
        ...(user.accountCreation ? { accountCreation: user.accountCreation } : {}),
      });
    }
  }
  return rows;
}

/** Highest active role a person holds here, or null (deactivated, or every grant revoked). */
function effectiveRoleOf(grants: AccessRow[], userStatus: UserStatus): RoleName | null {
  if (userStatus === "disabled") return null;
  let best: RoleName | null = null;
  for (const grant of grants) {
    if (!grant.isActive) continue;
    if (best === null || ROLE_RANK[grant.roleName] > ROLE_RANK[best]) best = grant.roleName;
  }
  return best;
}

/** Grants grouped back into people, in the order the server listed them. */
export function toPeople(rows: AccessRow[]): AccessPerson[] {
  const byUser = new Map<string, AccessPerson>();
  for (const row of rows) {
    let person = byUser.get(row.userId);
    if (!person) {
      person = {
        userId: row.userId,
        email: row.email,
        displayName: row.displayName,
        userStatus: row.userStatus,
        lastSignInAt: row.lastSignInAt,
        lastSignInMfa: row.lastSignInMfa ?? null,
        ...(row.lastActiveAt !== undefined ? { lastActiveAt: row.lastActiveAt } : {}),
        isSelf: row.isSelf,
        idpIssuer: row.idpIssuer,
        expectedIdpIssuer: row.expectedIdpIssuer,
        signInMethod: row.signInMethod ?? null,
        inviteWaiting: row.inviteWaiting ?? false,
        inviteUnconfirmed: row.inviteUnconfirmed ?? false,
        createdVia: row.createdVia ?? "grant",
        groupsClaimState: row.groupsClaimState ?? null,
        ...(row.accountCreation ? { accountCreation: row.accountCreation } : {}),
        grants: [],
        effectiveRole: null,
      };
      byUser.set(row.userId, person);
    }
    person.grants.push(row);
  }
  const people = [...byUser.values()];
  for (const person of people) {
    // Highest role first, so the chips read in the order they take effect.
    person.grants.sort((a, b) => ROLE_RANK[b.roleName] - ROLE_RANK[a.roleName]);
    person.effectiveRole = effectiveRoleOf(person.grants, person.userStatus);
  }
  return people;
}

/**
 * `allowed` is the screen's own permission check (#1067): false, the list is never asked for, so
 * a reader without `instance.users.read` reads nothing even where the route guard is bypassed.
 */
export function useAccessUsers(
  scope: AccessScope,
  filters: AccessListFilters = {},
  allowed = true,
) {
  const { selectedInstance, me } = useTenantInstance();
  const tenantId = selectedInstance?.tenantId ?? null;
  const path = accessUsersPath(scope, tenantId, filters);

  const query = useQuery<AccessUser[]>({
    ...listQueryOptions,
    queryKey: [path ?? NO_LIST_KEY],
    queryFn: async ({ signal }) => {
      // Unreachable: the query is disabled without a path.
      if (!path) throw new Error("No scope is selected");
      return apiRequest<AccessUser[]>(path, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      });
    },
    // Every one of these routes is instance-scoped by header, so asking before
    // `/api/v2/me` has chosen an instance sends a request with no `x-instance-id`
    // and earns a 400 that looks like a broken screen.
    enabled: allowed && Boolean(selectedInstance) && path !== null,
  });

  // A person matches the role filter when any grant of theirs does, and then
  // shows every grant, so the effective role is never computed from a subset.
  const people = useMemo(() => {
    const all = toPeople(toRows(query.data ?? [], me?.user.email));
    if (!filters.role || filters.role === "all") return all;
    return all.filter((person) => person.grants.some((grant) => grant.roleName === filters.role));
  }, [query.data, me?.user.email, filters.role]);

  return { ...query, path, people };
}

/**
 * The mutations a scope offers.
 *
 * Reactivating a grant and activating a USER are different acts on different
 * surfaces — `POST /access/grants/:id/reactivate` puts one grant back, while
 * `POST /platform/users/:id/activate` lifts a deactivation off the person — so
 * they are separate here rather than one "undo" the screen has to disambiguate.
 */
export function useAccessMutations(scope: AccessScope) {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  const tenantId = selectedInstance?.tenantId ?? null;
  const instanceId = selectedInstance?.id ?? null;

  const invalidate = useCallback(() => {
    // Every scope's list is keyed by its own path, and a grant made at tenant
    // scope shows up on the instance tab too, so refresh all three rather than
    // the one the screen happens to be on.
    void client.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          typeof key === "string" &&
          (key.startsWith("/api/v2/access/users") ||
            key.startsWith("/api/v2/platform/users") ||
            (key.startsWith("/api/v2/tenants/") && key.includes("/users")))
        );
      },
    });
  }, [client]);

  const grant = useMutation({
    mutationFn: async (input: GrantInput) => {
      const scopeType = scopeForRole(input.roleName);
      if (scope === "platform") {
        return apiRequest<AccessGrantResult>("/api/v2/platform/grants", {
          method: "POST",
          body: {
            email: input.email,
            displayName: input.displayName || undefined,
            roleName: input.roleName,
            scopeType,
            tenantId: scopeType === "tenant" ? tenantId : undefined,
            instanceId: scopeType === "instance" ? instanceId : undefined,
            // SEC-18: the platform surface always states why, whatever scope the
            // grant lands at, because that is the surface break-glass goes through.
            reason: input.reason,
            // S9: only this route accepts a provider; absent means the server default.
            expectedIssuer: input.expectedIssuer || undefined,
            replaceExisting: input.replaceExisting || undefined,
            signInMethod: input.signInMethod,
          },
        });
      }
      if (scope === "tenant") {
        return apiRequest<AccessGrantResult>(`/api/v2/tenants/${tenantId}/grants`, {
          method: "POST",
          body: {
            email: input.email,
            displayName: input.displayName || undefined,
            roleName: input.roleName,
            scopeType,
            instanceId: scopeType === "instance" ? instanceId : undefined,
            reason: input.reason || undefined,
            replaceExisting: input.replaceExisting || undefined,
            signInMethod: input.signInMethod,
          },
        });
      }
      return apiRequest<AccessGrantResult>("/api/v2/access/grants", {
        method: "POST",
        body: {
          email: input.email,
          displayName: input.displayName || undefined,
          roleName: input.roleName,
          scopeType,
          tenantId: scopeType === "tenant" ? tenantId : undefined,
          instanceId: scopeType === "instance" ? instanceId : undefined,
          reason: input.reason || undefined,
          replaceExisting: input.replaceExisting || undefined,
          signInMethod: input.signInMethod,
        },
      });
    },
    onSuccess: invalidate,
  });

  const revoke = useMutation({
    mutationFn: async (row: AccessRow) => {
      const path = grantPath(scope, tenantId, row.grantId);
      return apiRequest<void>(path, { method: "DELETE", body: {} });
    },
    onSuccess: invalidate,
  });

  const reactivate = useMutation({
    mutationFn: async (row: AccessRow) =>
      apiRequest(`/api/v2/access/grants/${row.grantId}/reactivate`, { method: "POST" }),
    onSuccess: invalidate,
  });

  // Round 7: a new invitation email for a person pending their first sign-in, on the grant's own
  // tab (`/resend-invite` at each scope). It changes no access, but the row's account and
  // invitation line can change (#1678: a request opened, or an invitation sent), so the list is
  // read again.
  const resendInvite = useMutation({
    mutationFn: async (row: AccessRow) => {
      const path = `${grantPath(scope, tenantId, row.grantId)}/resend-invite`;
      return apiRequest<ResendInviteView>(path, {
        method: "POST",
        body: {},
      });
    },
    onSuccess: invalidate,
  });

  const setUserActive = useMutation({
    mutationFn: async ({
      row,
      active,
      reason,
    }: {
      row: AccessRow;
      active: boolean;
      /** Recorded on the audit event; the deactivate dialog always asks for it (B-06). */
      reason?: string;
    }) =>
      apiRequest(`/api/v2/platform/users/${row.userId}/${active ? "activate" : "deactivate"}`, {
        method: "POST",
        body: reason ? { reason } : {},
      }),
    onSuccess: invalidate,
  });

  const rebind = useMutation({
    mutationFn: async (input: RebindInput) =>
      apiRequest(`/api/v2/platform/users/${input.userId}/rebind`, {
        method: "POST",
        body: {
          toIssuer: input.toIssuer,
          reason: input.reason,
          ...(input.allowSelf ? { allowSelf: true } : {}),
        },
      }),
    onSuccess: invalidate,
  });

  return { grant, revoke, reactivate, resendInvite, setUserActive, rebind };
}
